import React, { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Cpu, MemoryStick, HardDrive, Activity, Users, Database, Globe, Server, AlertTriangle, CheckCircle2, Clock } from "lucide-react";

const tone = (pct) => (pct >= 85 ? "#DC2626" : pct >= 60 ? "#D97706" : "#1E9E2A");
const fmtUp = (s) => { const d = Math.floor(s / 86400), h = Math.floor((s % 86400) / 3600), m = Math.floor((s % 3600) / 60); return d ? `${d}d ${h}h` : h ? `${h}h ${m}min` : `${m}min`; };
const gb = (mb) => (mb >= 1024 ? `${(mb / 1024).toFixed(1)} GB` : `${mb} MB`);

export const Spark = ({ values, color = "#1E9E2A", height = 44, max }) => {
  const w = 220;
  const top = max || Math.max(1, ...values);
  const pts = values.map((v, i) => `${(i / Math.max(1, values.length - 1)) * w},${height - (Math.min(v, top) / top) * (height - 4) - 2}`).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${height}`} className="w-full" style={{ height }} preserveAspectRatio="none">
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
};

const Gauge = ({ icon: Icon, label, pct, sub, hist }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
    <div className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Icon className="h-4 w-4" style={{ color: tone(pct) }} /> {label}</div>
    <div className="mt-2 font-display text-3xl font-bold" style={{ color: tone(pct) }}>{pct}%</div>
    <div className="h-2 rounded-full bg-slate-100 mt-2 overflow-hidden"><div className="h-full rounded-full transition-all duration-500" style={{ width: `${Math.min(100, pct)}%`, background: tone(pct) }} /></div>
    <div className="text-xs text-slate-500 mt-2">{sub}</div>
    {hist && hist.length > 1 && <div className="mt-2"><Spark values={hist} color={tone(pct)} max={100} /></div>}
  </div>
);

const Svc = ({ icon: Icon, name, ok, lines }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2 font-semibold text-slate-700"><Icon className="h-4 w-4 text-slate-500" /> {name}</div>
      <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full ${ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
        {ok ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />} {ok ? "No ar" : "Fora do ar"}
      </span>
    </div>
    <div className="mt-3 space-y-1 text-sm text-slate-600">{lines.map(([k, v]) => <div key={k} className="flex justify-between gap-3"><span className="text-slate-400">{k}</span><span className="font-medium text-slate-800 text-right">{v}</span></div>)}</div>
  </div>
);

export function useServerStats(everyMs = 5000) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  const hist = useRef({ cpu: [], mem: [], req: [] });
  const [, tick] = useState(0);
  useEffect(() => {
    let alive = true, timer;
    const load = async () => {
      if (document.hidden) { timer = setTimeout(load, everyMs); return; }
      try {
        const r = await api.get("/admin/server/stats");
        if (!alive) return;
        const h = hist.current;
        h.cpu = [...h.cpu, r.data.cpu.pct].slice(-60); h.mem = [...h.mem, r.data.memoria.usada_pct].slice(-60);
        setD(r.data); setErr(""); tick((x) => x + 1);
      } catch (e) { if (alive) setErr(formatApiError(e.response?.data?.detail) || "Sem resposta do servidor"); }
      timer = setTimeout(load, everyMs);
    };
    load();
    return () => { alive = false; clearTimeout(timer); };
  }, [everyMs]);
  return { d, err, hist: hist.current };
}

export const serverHealth = (d) => {
  if (!d) return { ok: false, txt: "Sem dados" };
  const probs = [];
  if (!d.mongo.ok) probs.push("Banco de dados");
  if (!d.web.ok) probs.push("Site (nginx)");
  if (d.cpu.pct >= 85) probs.push("CPU alta");
  if (d.memoria.usada_pct >= 90) probs.push("Memória alta");
  if (d.disco.usado_pct >= 85) probs.push("Disco cheio");
  if (d.requisicoes.erros_5xx > 0) probs.push(`${d.requisicoes.erros_5xx} erro(s) do servidor`);
  return { ok: probs.length === 0, txt: probs.length ? probs.join(" · ") : "Tudo normal" };
};

export default function ServerStatus() {
  const { d, err, hist } = useServerStats(5000);
  if (!d) return <div className="p-6 text-slate-500">{err || "Carregando…"}</div>;
  const h = serverHealth(d), r = d.requisicoes;
  const serie = r.serie.map((x) => x.req);
  return (
    <div className="space-y-6 animate-fade-up" data-testid="server-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><Server className="h-6 w-6 text-[#1E9E2A]" /> Servidor</h1>
          <p className="text-sm text-slate-500 mt-0.5">Números em tempo real (atualiza a cada 5 segundos). Em funcionamento há {fmtUp(d.uptime_s)}.</p>
        </div>
        <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold ${h.ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid="server-health">
          {h.ok ? <CheckCircle2 className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />} {h.txt}
        </span>
      </div>
      {err && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">Sem resposta agora: {err}</div>}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Gauge icon={Cpu} label="Processador" pct={d.cpu.pct} sub={`${d.cpu.nucleos} núcleos · carga ${d.cpu.carga.join(" / ")}`} hist={hist.cpu} />
        <Gauge icon={MemoryStick} label="Memória" pct={d.memoria.usada_pct} sub={`${gb(d.memoria.livre_mb)} livres de ${gb(d.memoria.total_mb)}`} hist={hist.mem} />
        <Gauge icon={HardDrive} label="Disco" pct={d.disco.usado_pct} sub={`${d.disco.usado_gb} GB usados de ${d.disco.total_gb} GB`} />
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center gap-2 text-sm font-semibold text-slate-600"><Users className="h-4 w-4 text-[#1E9E2A]" /> Online agora</div>
          <div className="mt-2 font-display text-3xl font-bold text-slate-900" data-testid="server-online">{d.online.m2}</div>
          <div className="text-xs text-slate-500 mt-1">ativos nos últimos 2 min · {d.online.m5} em 5 min · {d.online.m15} em 15 min</div>
          <div className="text-xs text-slate-400 mt-3">{d.alunos.total.toLocaleString("pt-BR")} alunos cadastrados · {d.alunos.lixeira} na lixeira</div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm lg:col-span-2">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 font-semibold text-slate-700"><Activity className="h-4 w-4 text-[#1E9E2A]" /> Acessos ao sistema por minuto (últimos 30 min)</div>
            <div className="text-sm text-slate-500"><strong className="text-slate-800">{r.ultimos_5min}</strong> em 5 min · tempo médio <strong className="text-slate-800">{r.tempo_medio_ms} ms</strong> · erros <span className={r.erros_5xx ? "text-red-600 font-bold" : ""}>{r.erros_4xx} (4xx) / {r.erros_5xx} (5xx)</span></div>
          </div>
          <div className="mt-3"><Spark values={serie} height={90} /></div>
          <div className="flex justify-between text-[11px] text-slate-400 mt-1"><span>há 30 min</span><span>agora</span></div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
          <div className="flex items-center gap-2 font-semibold text-slate-700"><Clock className="h-4 w-4 text-slate-500" /> Leitura rápida</div>
          <ul className="mt-3 space-y-2 text-sm text-slate-600 list-disc pl-5">
            <li>Verde: tudo folgado. Laranja: atenção (60% ou mais). Vermelho: crítico (85% ou mais).</li>
            <li>A carga do processador é boa enquanto ficar abaixo de {d.cpu.nucleos} (um por núcleo).</li>
            <li>Erros 5xx são falhas do servidor e merecem atenção; 4xx costumam ser senha errada ou link já usado.</li>
          </ul>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4">
        <Svc icon={Server} name="Backend (sistema)" ok={d.backend.ok} lines={[["Processos", d.backend.processos], ["Memória usada", gb(d.backend.memoria_mb)], ["Tempo médio", `${r.tempo_medio_ms} ms`]]} />
        <Svc icon={Database} name="Banco de dados" ok={d.mongo.ok} lines={d.mongo.ok ? [["Resposta", `${d.mongo.ms} ms`], ["Dados", gb(d.mongo.dados_mb)], ["Documentos", d.mongo.documentos.toLocaleString("pt-BR")], ["Coleções", d.mongo.colecoes]] : [["Erro", d.mongo.erro]]} />
        <Svc icon={Globe} name="Site (nginx)" ok={d.web.ok} lines={d.web.ok ? [["Resposta", `${d.web.ms} ms`], ["Código", d.web.status]] : [["Erro", d.web.erro || d.web.status]]} />
      </div>
    </div>
  );
}
